import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassName, twx } from '@/utils/tailwind';

export interface TabOption<TValue extends string> {
  value: TValue;
  /** Visible label. */
  label: string;
  /** Optional leading icon. */
  icon?: IconDefinition;
  /** Optional trailing badge — a number ("12 places") or short string. */
  count?: number | string;
  /** Optional accessible name override. Defaults to `label`. */
  ariaLabel?: string;
}

export interface TabsProps<TValue extends string> {
  options: ReadonlyArray<TabOption<TValue>>;
  value: TValue;
  onChange: (next: TValue) => void;
  /** Tab visual style. `underline` (default) suits panel-switching contexts
   * — full-width row, accent underline on the active tab. `pill` packs
   * the tabs into a soft bg with a filled active state, closer to the
   * SegmentedControl chrome. */
  variant?: 'underline' | 'pill';
  className?: ClassName;
}

const baseTabClasses =
  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-[colors,border-color] duration-150 cursor-pointer';

const variantClasses = {
  underline: {
    container: 'flex w-full border-b border-content-divider',
    tab: 'border-b-2 -mb-px',
    active: 'border-secondary-emphasized text-secondary-emphasized',
    inactive:
      'border-transparent text-content-subtle hover:text-content-text hover:border-content-border',
  },
  pill: {
    container: 'flex w-full p-0.5 rounded-xl bg-content-focal',
    tab: 'rounded-lg',
    active: 'bg-white text-secondary-emphasized shadow-sm',
    inactive: 'text-content-subtle hover:text-content-text',
  },
} as const;

/**
 * Panel-switching tab bar. Two or more options; one is active at a time.
 * Default `underline` variant suits the small-screen Scout bottom panel
 * (Places / Scouted) and any future place-detail-style sub-section
 * switchers. `pill` covers tighter cases where the underline would feel
 * stranded.
 */
export const Tabs = <TValue extends string>({
  options,
  value,
  onChange,
  variant = 'underline',
  className,
}: TabsProps<TValue>) => {
  const styles = variantClasses[variant];
  return (
    <div role="tablist" className={twx(styles.container, className)}>
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={option.ariaLabel ?? option.label}
            onClick={() => onChange(option.value)}
            className={twx(baseTabClasses, styles.tab, isActive ? styles.active : styles.inactive)}
          >
            {option.icon && (
              <FontAwesomeIcon icon={option.icon} style={{ width: 12, height: 12 }} />
            )}
            <span>{option.label}</span>
            {option.count !== undefined && (
              <span
                className={twx(
                  'inline-flex items-center justify-center min-w-[20px] px-1.5 h-4 rounded-full text-[10px] font-semibold',
                  isActive
                    ? 'bg-secondary-emphasized text-white'
                    : 'bg-content-focal text-content-subtle',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
